<script setup lang="ts">
// Inlined original ResetPasswordAdmin.vue
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { forgotPasswordAdmin, resetPasswordAdmin } from '@/services/authSignin'

const router = useRouter()
const { xs } = useDisplay()
const username = ref('')
const code = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const step = ref(1)
const loading = ref(false)
const message = ref('')
const snackbar = ref(false)
const showPassword = ref(false)
const showPasswordConfirm = ref(false)

const handleSendCode = async () => {
  loading.value = true
  try {
    await forgotPasswordAdmin(username.value)
    message.value = '✅ Verification code sent! Check your email.'
    step.value = 2
  } catch (err: any) {
    message.value = err.message || '❌ Failed to send code.'
  } finally {
    snackbar.value = true
    loading.value = false
  }
}
const handleReset = async () => {
  loading.value = true
  if (confirmPassword.value !== newPassword.value) {
    message.value = '❌ New password and confirmation do not match.'
    snackbar.value = true
    loading.value = false
    return
  }
  try {
    await resetPasswordAdmin(username.value, code.value, newPassword.value)
    message.value = '✅ Password reset successful! Redirecting to login...'
    setTimeout(() => router.push('/company/login'), 1500)
  } catch (err: any) {
    message.value = err.message || '❌ Reset failed.'
  } finally {
    snackbar.value = true
    loading.value = false
  }
}
</script>

<template>
  <v-container fluid class="page-wrapper">
    <div style="position: absolute; right: 20px; top: 20px">
      <v-btn color="#3677D3" class="login-btn" @click="router.back()" aria-label="Go back">
        <v-icon>mdi-arrow-left</v-icon>
        &nbsp; Go Back
      </v-btn>
    </div>

    <div class="auth-container">
      <h2 class="brand">SodaSoft.io</h2>
      <v-card class="auth-card" :max-width="xs ? '100%' : 440">
        <div class="text-center mb-8">
          <h1 class="welcome">
            {{ step === 1 ? 'Forgot Password Company' : 'Reset Password Company' }}
          </h1>
          <p class="subtitle">
            <span v-if="step === 1">
              Enter your username and we’ll send you a verification code.
            </span>
            <span v-else>Enter the code you received and choose a new password.</span>
          </p>
        </div>

        <v-form @submit.prevent="step === 1 ? handleSendCode() : handleReset()">
          <div class="input-group" v-if="step === 1">
            <input v-model="username" type="text" placeholder="Username" class="input" />
          </div>

          <div class="input-group" v-else>
            <input v-model="code" type="text" placeholder="Verification Code" class="input" />

            <div class="password-wrapper">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="newPassword"
                placeholder="Password"
                class="input"
              />
              <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>

            <div class="password-wrapper">
              <input
                :type="showPasswordConfirm ? 'text' : 'password'"
                v-model="confirmPassword"
                placeholder="Confirm Password"
                class="input"
              />
              <button
                type="button"
                class="eye-btn"
                @click="showPasswordConfirm = !showPasswordConfirm"
              >
                <i :class="showPasswordConfirm ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>

          <v-btn
            block
            color="#3D7FD8"
            :disabled="loading"
            type="submit"
            height="48"
            class="login-btn mt-5"
          >
            <span v-if="!loading">{{ step === 1 ? 'Send Code' : 'Reset Password' }}</span>
            <v-progress-circular v-else indeterminate size="20" color="white" />
          </v-btn>
        </v-form>

        <v-snackbar v-model="snackbar" color="info" :timeout="3000">
          {{ message }}
          <template #action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
          </template>
        </v-snackbar>
      </v-card>
    </div>
  </v-container>
</template>

<style scoped>
.page-wrapper {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #cacaca;
  padding: 24px 16px;
}

.auth-container {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 28px;
  text-align: left;
  align-self: flex-start;
}

.auth-card {
  width: 100%;
  border-radius: 16px;
  padding: 44px 36px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.welcome {
  font-size: 32px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.2;
}

.subtitle {
  font-size: 14px;
  color: #5a5a5a;
  margin-top: 8px;
  line-height: 1.5;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 4px;
}

.input {
  width: 100%;
  height: 48px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0 16px;
  font-size: 14px;
  color: #1a1a1a;
  background: white;
  transition: border-color 0.2s;
}

.input:focus {
  outline: none;
  border-color: #4a90e2;
}

.input::placeholder {
  color: #aaaaaa;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-wrapper .input {
  padding-right: 48px;
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
}

.eye-btn:hover {
  color: #333;
}

.eye-btn .mdi {
  font-size: 20px;
}

.login-btn {
  border-radius: 8px;
  text-transform: none;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  box-shadow: none !important;
}
</style>
